import React, {useRef, useEffect, useMemo, useState, useImperativeHandle, forwardRef} from 'react';
import {merge} from 'lodash';
import * as echarts from 'echarts/core';
import {BarChart} from 'echarts/charts';
import {
    TooltipComponent,
    TitleComponent,
    LegendComponent,
    ToolboxComponent,
    GridComponent,
    DataZoomComponent
} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';
import {Row, Col, Empty} from 'acud';
import classNames from 'classnames';

import ChartBarEmpty from './ChartBarEmpty';
import {ACUD_PRO_PREFIX} from '../../../_common/constants';
import {CHART_COLOR} from '../util';

import '../index.less';

const CHART_PREFIX = `${ACUD_PRO_PREFIX}-chart`;

// 注册必需的图表和组件
echarts.use([
    BarChart,
    TooltipComponent,
    TitleComponent,
    LegendComponent,
    ToolboxComponent,
    GridComponent,
    CanvasRenderer,
    DataZoomComponent
]);

interface CustomLegend {
    colNum?: number;
}

interface EmptyData {
    icon?: React.ReactNode;
    extra?: React.ReactNode;
}

export interface ChartBarProps {
    containerWidth?: number | string;
    containerHeight?: number | string;
    chartWidth?: number | string;
    chartHeight?: number | string;
    option?: any;
    customLegend?: boolean | CustomLegend;
    emptyData?: EmptyData | boolean;
}


const DEFAULT_OPTION = {
    title: {
    },
    tooltip: {
        trigger: 'axis',
        enterable: true,
        confine: true
    },
    legend: {
        icon: 'roundRect',
        itemWidth: 12,
        itemHeight: 2
    },
    xAxis: {
        triggerEvent: true,
        type: 'category'
    },
    yAxis: {
        type: 'value'
    },
    grid: {
        left: 10,
        containLabel: true,
        right: 10,
        top: 60
    }
};
const DEFAULT_OPTION_SERIES = {
    type: 'bar',
    showSymbol: false,
    smooth: true,
    emphasis: {
        focus: 'series'
    },
    itemStyle: {
        borderRadius: [2, 2, 0, 0]
    }
};

const ChartBar = forwardRef((props: ChartBarProps, ref) => {
    const {
        containerWidth,
        containerHeight,
        chartWidth,
        chartHeight,
        option,
        customLegend = false,
        emptyData = false
    } = props;
    const chartRef = useRef(null);
    const chartInsRef = useRef(null);
    const [mergedOption, setMergedOption] = useState<any>();
    const [legendSelected, setLegendSelected] = useState<any>({});
    const [colorMap, setColorMap] = useState<any>({});

    const colSpan = useMemo(() => {
        if (customLegend === false || customLegend === true) {
            return 24;
        }
        const colNum = customLegend?.colNum;
        if (typeof colNum !== 'number') {
            return 24;
        }
        return Math.round(24 / colNum);
    }, [customLegend]);

    useEffect(() => {
        if (!option) {
            return;
        }
        let mergedOption: any = {};
        if (option) {
            merge(mergedOption, DEFAULT_OPTION, option);
            // series 需要遍历处理
            mergedOption.series = mergedOption.series.map((s: any) => {
                return merge({}, DEFAULT_OPTION_SERIES, s);
            });
        }
        if (customLegend !== false) {
            const {series} = mergedOption;
            const legendSelectedTemp: any = {};
            const colorMapTemp: any = {};
            for (let i = 0, len = series.length; i < len; i++) {
                const element = series[i];
                legendSelectedTemp[element.name] = true;
                colorMapTemp[element.name] = CHART_COLOR[i % CHART_COLOR.length];
            }
            setLegendSelected(legendSelectedTemp);
            setColorMap(colorMapTemp);
            mergedOption.legend.show = false;
            mergedOption.selected = legendSelectedTemp;
            // series 需要遍历处理
            mergedOption.series = mergedOption.series.map((s: any) => {
                if (!s.itemStyle) {
                    s.itemStyle = {};
                }
                s.itemStyle.color = colorMapTemp[s.name];
                return s;
            });
        }
        setMergedOption(mergedOption);
    }, [option, customLegend]);

    useEffect(() => {
        if (!chartInsRef.current) {
            let opt: any = {
                locale: 'ZH'
            };
            if (chartWidth) {
                opt.width = chartWidth;
            }
            if (chartHeight) {
                opt.height = chartHeight;
            }
            // 创建图表实例
            const chart = echarts.init(chartRef.current, null, opt);
            chartInsRef.current = chart as any;
            if (chartWidth === undefined && chartHeight === undefined) {
                const resizeObserver = new ResizeObserver(() => {
                    (chartInsRef.current as any).resize();
                });
                resizeObserver.observe(chartRef.current as any);
                return () => {
                    resizeObserver.disconnect();
                };
            }
        }
        else {
            if (chartWidth !== undefined || chartHeight !== undefined) {
                (chartInsRef.current as any).resize({
                    width: chartWidth,
                    height: chartHeight
                });
            }
        }
    }, [chartWidth, chartHeight]);

    useEffect(() => {
        if (!mergedOption) {
            return;
        }
        const fOption = {...mergedOption};
        if (legendSelected) {
            fOption.legend.selected = legendSelected;
        }
        (chartInsRef?.current as any)?.setOption(fOption, true);
    }, [mergedOption, legendSelected]);

    const handelLegendClick = (legendItem: string, value: boolean) => {
        const legendSelectedTemp = {...legendSelected};
        legendSelectedTemp[legendItem] = !value;
        setLegendSelected(legendSelectedTemp);
    };

    const containerStyle = useMemo(() => {
        let cStyle: any = null;
        if (containerWidth) {
            cStyle = {width: containerWidth};
            if (containerHeight) {
                cStyle.height = containerHeight;
            }
        }
        else if (containerHeight) {
            cStyle = {height: containerHeight};
        }

        return cStyle;
    }, [containerWidth, containerHeight]);

    const emptyCom = useMemo(() => {
        if (emptyData) {
            if (emptyData === true) {
                return (
                    <div className={`${CHART_PREFIX}-empty-box`}>
                        <Empty
                            image={<ChartBarEmpty width={72} height={72} />}
                        />
                    </div>
                );
            }
            const {icon, extra} = emptyData;
            return (
                <div className={`${CHART_PREFIX}-empty-box`}>
                    <Empty
                        image={(icon || <ChartBarEmpty width={72} height={72} />)}
                        description={extra || '暂无数据'}
                    />
                </div>
            );
        }
        return null;
    }, [emptyData]);

    useImperativeHandle(ref, () => ({
        getChartInstance: () => {
            return chartInsRef.current;
        }
    }));

    return (
        <div className={`${CHART_PREFIX}-box`}>
            <div ref={chartRef} className={`${CHART_PREFIX}-box-content`} style={containerStyle} />
            {emptyCom}
            {customLegend && (
                <Row align="top" justify="start" className={`${CHART_PREFIX}-legend-container`}>
                    {Object.entries(legendSelected).map(([legendItem, value]) => {
                        return (
                            <Col
                                span={colSpan}
                                className={`${CHART_PREFIX}-legend-item`}
                                onClick={() => handelLegendClick(legendItem, value as boolean)}
                                key={legendItem}
                            >
                                <div
                                    className={`${CHART_PREFIX}-legend-tag`}
                                    style={{backgroundColor: colorMap[legendItem]}}
                                >
                                </div>
                                <div
                                    className={classNames(`${CHART_PREFIX}-legend-name`, {
                                        [`${CHART_PREFIX}-legend-name-disabled`]: value === false
                                    })}
                                >{legendItem}
                                </div>
                            </Col>
                        );
                    })}
                </Row>)}
        </div>
    );
});

ChartBar.displayName = 'ChartBar';

export default ChartBar;
